import 'isomorphic-fetch';
import { camelizeKeys, pascalizeKeys } from 'humps';
import queryString from 'query-string';
import { cloneDeep } from 'lodash';

export const CALL_API = Symbol('Call API');
export const CONTENT_TYPE = {
  JSON: Symbol('json'),
  FORM: Symbol('form'),
};

// OPTI-38 韌性：auth seed store（Redis）瞬斷時，正常已登入的請求會偶發回 403
// （[NoSeed] Token is gone or invalid）。原行為「任何 403 → 立刻登出」會把人因瞬時 infra
// blip 強制踢出。這裡對 403 退避 retry 一次：
//   - 瞬時 blip → retry 成功 → 不誤登出。
//   - 真失效/過期（含天級租戶正常過期）→ retry 仍 403 → 最終行為不變（照常往下走 → 登出）。
// 零回歸：非 403 完全不 retry；403 的請求是被 auth 擋下、未執行業務邏輯，retry 安全（含 POST）。
const RETRY_403_BACKOFF_MS = 400;
function fetchWithRetryOn403(fullUrl, fetchOptions) {
  return fetch(fullUrl, fetchOptions).then(response => {
    if (Number(response.status) !== 403) return response;
    return new Promise(resolve => setTimeout(resolve, RETRY_403_BACKOFF_MS))
      .then(() => fetch(fullUrl, fetchOptions));
  });
}

function callApi(url, params, method = 'GET', type = CONTENT_TYPE.JSON, headers = {}, shouldCamelizedJson = true) {
  let fullUrl = url;
  const fetchOptions = {
    mode: 'cors',
    method,
    headers: cloneDeep(headers),
    credentials: 'include',
  };

  switch (type) {
    case CONTENT_TYPE.JSON:
      fetchOptions.headers['Content-Type'] = 'application/json';
      if (typeof(params) !== 'undefined' && method === 'GET') {
        fullUrl += '?' + queryString.stringify(params);
      } else if (typeof params !== 'undefined') {
        fetchOptions.body = JSON.stringify(pascalizeKeys(params));
      }
      break;
    case CONTENT_TYPE.FORM:
      if (typeof(params) !== 'undefined') {
        fetchOptions.body = params;
      }
      break;
    default:
      break;
  }

  return fetchWithRetryOn403(fullUrl, fetchOptions)
  .then(response => {
    const resStatus = Number(response.status);
    if (!response.ok && resStatus === 403) {
      // OPTI-108：能走到這裡代表「退避 retry 後仍是 403」= 確定失效（已排除 infra blip），
      // 是中央唯一「確定要登出」的點。把分鐘級登出收斂在此（而非 render 層 side-effect）。
      // 分鐘級 new mode 才有 tokenExpireAt（須在 logout 清 localStorage 前讀）：
      //   - 分鐘級：跳過 interim「您已登出系統」modal，直接導向 idleTimeout 登出，並中止後續鏈。
      //   - 天級：無此 key，維持原行為（往下 reject → App 顯示 modal 按確定才登出），零回歸。
      const isMinuteLevel = !!window.localStorage.getItem('tokenExpireAt');
      if (isMinuteLevel) {
        // 延遲 require：靜態 import 會形成 middlewares/api → userReducer → configureStore → api
        // 的循環（configureStore 第 4 行靜態 import api，且 userReducer 於 module 初始化即用到
        // configureStore 匯出的 SUCCESS），init 期會讀到 undefined SUCCESS。改用 call-time require
        // 切斷 init 期循環，執行時 module graph 已完整。
        try {
          const { logout } = require('containers/App/reducers/userReducer'); // eslint-disable-line global-require
          logout(undefined, 'idleTimeout');
        } catch (e) {
          // logout 失敗 fallback：硬重載觸發重新 SSO 導回登入頁，避免下方永掛 promise 白畫面（9527 ASYNC1）
          // eslint-disable-next-line no-console
          console.error('[OPTI-108] idle logout failed, fallback reload', e);
          window.location.reload();
        }
        return new Promise(() => {}); // 中止鏈：頁面即將導向（成功 logout 或 fallback reload 都會 unload），避免閃現 error modal
      }
      return {
        json: {
          error: {
            'status': resStatus,
            'httpStatus': resStatus,
          },
        },
        response,
      };
    }

    return response.json().then(json => {
      return { json, response };
    });
  })
  .then(({ json, response }) => {
    const resStatus = Number(response.status);
    const camelizedJson = camelizeKeys(json);
    if (typeof(camelizedJson.error) !== 'undefined') {
      return Promise.reject({
        'status': camelizedJson.error.status,
        'message': camelizedJson.error.detail,
        'httpStatus': resStatus,
        'others': camelizedJson.error.mulitiDetail,
      });
    }

    if (!response.ok) {
      return Promise.reject({
        'status': resStatus,
        'message': response.statusText,
        'httpStatus': resStatus,
      });
    }
    if (!shouldCamelizedJson) {
      return json;
    }
    return camelizedJson;
  });
}

export default store => next => action => {
  const { app: { application: { headers } } } = store.getState();
  const callAPI = action[CALL_API];

  if (typeof(callAPI) === 'undefined') {
    return next(action);
  }

  function actionWith(data) {
    const finalAction = Object.assign({}, action, data);
    delete finalAction[CALL_API];
    return finalAction;
  }

  const { types, params, method, contentType, url, onSuccess, onFail, shouldCamelizedJson } = callAPI;
  const [requestType, successType, failureType] = types;
  next(actionWith({
    type: requestType,
  }));

  return callApi(url, params, method, contentType, Object.assign({}, callAPI.headers, headers), shouldCamelizedJson).then(
      response => {
        next(actionWith({
          response,
          type: successType,
        }));

        if (typeof onSuccess === 'function') {
          onSuccess(response);
        }

        return Promise.resolve(response);
      },
      (error) => {
        next(actionWith({
          type: failureType,
          error,
        }));

        if (typeof onFail === 'function') {
          onFail(error);
        }

        return Promise.reject(error);
      }
  );
};



/** WEBPACK FOOTER **
 ** D:/a/1/s/src/middlewares/api.js
 **/